import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Grid, Row, Column } from '@lux/components';
import FILTERS from '../../config/filters';
import Anchor from '../Anchor';
import { BodySecondary, H4, HR } from '../Base';
import Button from '../Button';
import Modal from '../Modal';
import PhoneCatalogFilter from '../PhoneCatalogFilter';

import usePhoneCatalogFilter, {
  FILTER_ACTIONS
} from '../../hooks/usePhoneCatalogFilter';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import { useBrowserBack } from '../../hooks/useBrowserBack/useBrowserBack';
import useTranslate from '../../hooks/useTranslation';

const StyledGrid = styled(props => <Grid {...props} />)`
  padding: 0;
  ${p => p.theme.media.md`
    padding: 0 ${p => p.theme.spacing(1)};
  `};
`;

const StyledRow = styled(props => <Row {...props} />)`
  margin: 0;
`;

const StyledColumn = styled(props => <Column {...props} />)`
  padding: 0;
`;

const StyledTitle = styled(H4)`
  margin-bottom: ${p => p.theme.spacing(3)};
`;

const StyledClearText = styled(BodySecondary)`
  margin: ${p => p.theme.spacing(2)} 0;
`;

const StyledButton = styled(Button)`
  margin-bottom: ${p => p.theme.spacing(12)};
`;

export const PhoneCatalogFilterSection = ({
  allCategories,
  allBrands,
  allInstalmentOptions,
  cisPlanRates,
  phones,
  showFilterModal,
  onFilterModalToggle,
  onFilterChange,
  selectedPlan,
  defaultFilter,
  location
}) => {
  const { t } = useTranslate();

  const {
    categoriesFilter,
    brandsFilter,
    ippFilter,
    setFilter,
    categoriesList,
    brandsList,
    ippOptionList
  } = usePhoneCatalogFilter({
    phones,
    allBrands,
    allInstalmentOptions,
    allCategories,
    cisPlanRates,
    onFilterChange,
    defaultFilter,
    location
  });

  const handleChangeFilterAfterBackPress = () => {
    handleIppFilter();
  };
  useEffect(() => {
    handleIppFilter();
  }, []);

  const handleIppFilter = () => {
    if (selectedPlan && isSimOnlyPlan(selectedPlan)) {
      handleIppFilterChange({ [FILTERS.IPP_OPTIONS]: true });
    } else {
      // To clear ipp filter set it to undefined.
      handleIppFilterChange({ [FILTERS.IPP_OPTIONS]: undefined });
    }
  };

  useBrowserBack(handleChangeFilterAfterBackPress);

  const handleCategoriesFilterChange = e => {
    setFilter(FILTER_ACTIONS.SET_CATEGORIES, e);
  };

  const handleBrandsFilterChange = e => {
    setFilter(FILTER_ACTIONS.SET_BRANDS, e);
  };

  const handleIppFilterChange = e => {
    setFilter(FILTER_ACTIONS.SET_IPP_OPTIONS, e);
  };

  const handleClearFilters = () => {
    setFilter(FILTER_ACTIONS.RESET);
    if (selectedPlan && isSimOnlyPlan(selectedPlan)) {
      handleIppFilterChange({ [FILTERS.IPP_OPTIONS]: false });
    }
  };

  return (
    <StyledGrid>
      <StyledRow>
        <StyledColumn sm={false} md={12}>
          <StyledTitle>{t('FILTER')}</StyledTitle>
          {allInstalmentOptions && (
            <>
              <HR />
              <PhoneCatalogFilter
                active
                title={t('ZERO_INTEREST_INSTALMENT_PLAN')}
                items={ippOptionList}
                checkedItems={ippFilter}
                selectedPlan={selectedPlan}
                onChange={e => handleIppFilterChange(e)}
              />
            </>
          )}
          <HR />
          <PhoneCatalogFilter
            active
            title={t('CATEGORIES')}
            items={categoriesList}
            checkedItems={categoriesFilter}
            selectedPlan={selectedPlan}
            onChange={e => handleCategoriesFilterChange(e)}
          />
          <HR />
          <PhoneCatalogFilter
            active
            title={t('BRANDS')}
            items={brandsList}
            checkedItems={brandsFilter}
            selectedPlan={selectedPlan}
            onChange={e => handleBrandsFilterChange(e)}
          />
          <HR />
          <StyledClearText>
            <Anchor data-testid="clear-filter" onClick={handleClearFilters}>
              {t('CLEAR')}
            </Anchor>
          </StyledClearText>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn sm={12} md={false}>
          <Modal
            open={showFilterModal}
            title={t('FILTER')}
            onClose={() => onFilterModalToggle(!showFilterModal)}
          >
            {allInstalmentOptions && (
              <>
                <PhoneCatalogFilter
                  title={t('ZERO_INTEREST_INSTALMENT_PLAN')}
                  items={ippOptionList}
                  checkedItems={ippFilter}
                  selectedPlan={selectedPlan}
                  onChange={e => handleIppFilterChange(e)}
                />
                <HR />
              </>
            )}

            <PhoneCatalogFilter
              title={t('CATEGORIES')}
              items={categoriesList}
              checkedItems={categoriesFilter}
              selectedPlan={selectedPlan}
              onChange={e => handleCategoriesFilterChange(e)}
            />
            <HR />
            <PhoneCatalogFilter
              title={t('BRANDS')}
              items={brandsList}
              checkedItems={brandsFilter}
              selectedPlan={selectedPlan}
              onChange={e => handleBrandsFilterChange(e)}
            />
            <HR />
            <StyledClearText>
              <Anchor onClick={handleClearFilters}>Clear</Anchor>
            </StyledClearText>
            <StyledButton
              fullWidth={true}
              onClick={() => onFilterModalToggle(false)}
            >
              {t('APPLY')}
            </StyledButton>
          </Modal>
        </StyledColumn>
      </StyledRow>
    </StyledGrid>
  );
};

PhoneCatalogFilterSection.propTypes = {
  allBrands: PropTypes.array,
  allInstalmentOptions: PropTypes.array,
  allCategories: PropTypes.array,
  cisPlanRates: PropTypes.array,
  phones: PropTypes.array,
  showFilterModal: PropTypes.bool,
  onFilterModalToggle: PropTypes.func,
  onFilterChange: PropTypes.func
};

export default PhoneCatalogFilterSection;
